import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';

interface IGetAllAccountBalanceParams {
  page: number;
  perPage: number;
  search?: string;
}

interface IGetAllAccountBalanceResponse {
  status?: boolean;
  message?: string;
  data?: {
    accounts: {
      outletId: string;
      outletName: string;
      accountName: string;
      accountNumber: string;
      accountType: string;
      ledgerBalance: number;
      availableBalance: number;
      withdrawableBalance: number;
      commissionsAccountNumber: string;
    }[];
    summary: {
      totalLedgerBalance: number;
      totalAvailableBalance: number;
      totalWithdrawableBalance: number;
    };
  };
  meta: {
    total: number;
    perPage: number;
    page: number;
    totalPages: number;
  };
  errors?: IApiResponseError;
}

const initialState: IApiState<IGetAllAccountBalanceResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const getAllAccountBalanceAsync = createAsyncThunk(
  'allAccountBalance/get',
  async (
    { page, perPage, search }: IGetAllAccountBalanceParams,
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;
      const businessId = localStorage.getItem('businessId');

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      let url = `/accounts/balances?page=${page}&perPage=${perPage}`;

      if (search) {
        url += `&search=${search}`;
      }

      const { data } = await api.get(url, config);

      if (!search) {
        localStorage.removeItem('totalAvailableBalance');
        localStorage.removeItem('totalAccounts');
        const summaryData = (data as IGetAllAccountBalanceResponse)?.data
          ?.summary;
        const metaData = (data as IGetAllAccountBalanceResponse)?.meta;

        localStorage.setItem(
          'totalAvailableBalance',
          summaryData?.totalAvailableBalance?.toString() as string,
        );
        localStorage.setItem(
          'totalAccounts',
          metaData?.total.toString() as string,
        );
      }

      return data;
    } catch (err) {
      const error = err as AxiosError<IGetAllAccountBalanceResponse>;

      return rejectWithValue(error.response?.data);
    }
  },
);

export const allAccountBalanceFetch = createSlice({
  name: 'allAccountBalance/fetch',
  initialState,
  reducers: {
    resetAllAccountBalance: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllAccountBalanceAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(getAllAccountBalanceAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IGetAllAccountBalanceResponse;
      })
      .addCase(getAllAccountBalanceAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IGetAllAccountBalanceResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetAllAccountBalance } = allAccountBalanceFetch.actions;
export default allAccountBalanceFetch.reducer;
